import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { BimChapterEnum } from 'gql/graphql';
import type { BimPriceLevelEnum, BimWorkOrderPriceData, BimWorkOrderTotalItemData } from 'gql/graphql';
import { BimWorkOrderRowTypeEnum, getDetailsInPriceTableRequestId } from 'pages/Project/BimProject/WorkOrder/types';
import { isNotNil } from 'utils';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderVersionTotalsRow } from '../../workOrderVersionQueries';

export const useBimWorkOrderVersionTotalsRow = ({
    workOrderVersionId,
    constructionTypeId,
    chapter,
    workOrderObjectId,
    useInPrices = true,
    checkedPriceRowForDetails,
    withEffect = true,
    resetFilterData,
    priceLevel,
}: {
    workOrderVersionId?: number;
    constructionTypeId: number | undefined;
    chapter: BimChapterEnum | undefined;
    workOrderObjectId: number | undefined;
    useInPrices?: boolean;
    checkedPriceRowForDetails?: {
        rowType: BimWorkOrderRowTypeEnum;
        data: BimWorkOrderTotalItemData | BimWorkOrderPriceData | null;
    } | null;
    withEffect?: boolean;
    resetFilterData?: () => void;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const dispatch = useDispatch();

    const isTotalsDetails = useMemo(
        () => !!checkedPriceRowForDetails && checkedPriceRowForDetails.rowType === BimWorkOrderRowTypeEnum.TotalsString,
        [checkedPriceRowForDetails],
    );

    const totalsRowVars = useMemo(
        () => ({
            workOrderVersionId,
            constructionTypeId,
            chapter,
            workOrderVersionObjectId: workOrderObjectId,
            useInPrices,
            ...(chapter !== BimChapterEnum.Others ? { priceLevel } : undefined),
        }),
        [workOrderVersionId, constructionTypeId, chapter, workOrderObjectId, useInPrices, priceLevel],
    );

    const isRequestLegit = useMemo(() => {
        const variablesFull =
            Object.values(totalsRowVars).every((key) => isNotNil(key)) &&
            totalsRowVars.workOrderVersionId === workOrderVersionId;

        if (isTotalsDetails && !useInPrices) {
            return variablesFull && isTotalsDetails;
        }

        if (useInPrices) {
            return variablesFull;
        }

        return false;
    }, [isTotalsDetails, totalsRowVars, useInPrices, workOrderVersionId]);

    const requestId = getDetailsInPriceTableRequestId(useInPrices);

    const [makeResetFetchedData] = useResetStoreData({
        requestId,
    });

    useEffect(() => {
        makeResetFetchedData(undefined);
    }, [makeResetFetchedData, totalsRowVars]);

    const [rowTotals, rowTotalsDataStatus, , rowTotalsDataRequest] = useRequest({
        requestId,
        query: bimWorkOrderVersionTotalsRow,
        variables: totalsRowVars,
        withEffect: isRequestLegit && withEffect,
    });

    const makeRefreshDetailsData = useCallback(() => {
        dispatch(rowTotalsDataRequest(totalsRowVars));

        if (resetFilterData) {
            resetFilterData();
        }
    }, [dispatch, resetFilterData, rowTotalsDataRequest, totalsRowVars]);

    const isLoading = rowTotalsDataStatus === RequestStatus.FETCHING;

    const rowTotalsData = useMemo(
        () =>
            rowTotals?.bimWorkOrderVersionTotals?.items?.map((i) => ({
                ...i,
                planValueOthers: i.planValue.others,
                planValueTotal: i.planValue.total,
                isTotalsRow: true,
            })) ?? [],
        [rowTotals?.bimWorkOrderVersionTotals?.items],
    );

    return {
        rowTotalsData,
        isLoading,
        makeRefreshDetailsData,
    };
};
